import { FC, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import ModalDelete from "src/components/common/ModalDelete";
import { SftpKey } from "@models/SftpSettings.model";
import useOrganizationSftp from "../hooks/useOrganizationSftp";
import { Paths } from "src/paths";
import { SFTP_INSTRUCTIONS_PAGE, useFeatureFlag } from "src/shared/featureFlags/useFeatureFlag";

interface Props {
  organizationId: number;
  /** Notified after keys change, so hosts can refresh derived state. */
  onKeysChanged?: () => void;
}

const PRIVATE_KEY_MARKER = "PRIVATE KEY";

const OrganizationSftpTab: FC<Props> = ({ organizationId, onKeysChanged }) => {
  const { settings, isLoading, isSaving, error, uploadKey, deleteKey, downloadInstructions } =
    useOrganizationSftp(organizationId);
  const instructionsPageOn = useFeatureFlag(SFTP_INSTRUCTIONS_PAGE);
  const instructionsHref = `${import.meta.env.BASE_URL}${Paths.SFTP_INSTRUCTIONS.replace(":organizationId", String(organizationId))}`;

  const [keyText, setKeyText] = useState("");
  const [label, setLabel] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SftpKey | null>(null);

  const handleFileChosen = async (file: File | undefined) => {
    if (!file) return;
    setKeyText(await file.text());
    setLabel(file.name);
  };

  const handleCopyUsername = async () => {
    if (!settings) return;
    await navigator.clipboard.writeText(settings.sftp_user);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    const body = keyText.trim();
    if (!body) return;
    if (body.includes(PRIVATE_KEY_MARKER)) {
      setFormError("Upload your PUBLIC key (the .pub file) — never your private key.");
      return;
    }
    setFormError(null);
    try {
      await uploadKey({ body, label: label.trim() || undefined });
      setKeyText("");
      setLabel("");
      onKeysChanged?.();
    } catch {
      // uploadKey already surfaced the server's error via the toast.
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteKey(deleteTarget.id);
      onKeysChanged?.();
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading && !settings) {
    return <Loader />;
  }

  return (
    <div>
      <div className="mb-4">
        <h5 className="mb-1">SFTP Settings</h5>
        <p className="text-muted small mb-0">
          Manage this organization's SFTP access for file uploads.
        </p>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {settings && (
        <div className="d-flex flex-wrap gap-5 mb-4">
          <div>
            <div className="text-muted small mb-1">SFTP Username</div>
            <div className="d-flex align-items-center gap-2">
              <code className="font-monospace bg-light border rounded px-2 py-1">{settings.sftp_user}</code>
              <Button variant="link" size="sm" className="text-decoration-none" onClick={handleCopyUsername}>
                <i className="bi bi-clipboard me-1" />
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
          <div>
            <div className="text-muted small mb-1">SFTP Host</div>
            <code className="font-monospace bg-light border rounded px-2 py-1 d-inline-block">{settings.sftp_host}</code>
          </div>
        </div>
      )}

      <div className="border rounded p-3 mb-4">
        <h6 className="mb-1">Upload public key</h6>
        <p className="text-muted small mb-3">Upload your public key to enable file sharing via SFTP.</p>

        {formError && <Alert variant="danger" className="mb-3">{formError}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Choose file (.pub)</Form.Label>
          <Form.Control
            type="file"
            accept=".pub,.txt"
            onChange={(e) => handleFileChosen((e.target as HTMLInputElement).files?.[0])}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Or paste your public key</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            className="font-monospace"
            placeholder="ssh-ed25519 AAAA..."
            value={keyText}
            onChange={(e) => setKeyText(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Label</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. laptop.pub"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Form.Group>

        <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!keyText.trim() || isSaving}>
          <i className="bi bi-upload me-1" />
          Upload key
        </Button>
      </div>

      <h6 className="mb-3">Existing keys</h6>
      {!settings || settings.keys.length === 0 ? (
        <div className="text-muted text-center py-4 border rounded mb-4">
          No public keys uploaded yet.
        </div>
      ) : (
        <Table hover responsive className="align-middle mb-4">
          <thead>
            <tr>
              <th>Label</th>
              <th>Type</th>
              <th>Fingerprint</th>
              <th>Added</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {settings.keys.map((key) => (
              <tr key={key.id}>
                <td className="fw-semibold">{key.label}</td>
                <td>{key.key_type}</td>
                <td className="font-monospace small">{key.fingerprint}</td>
                <td>{new Date(key.create_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</td>
                <td className="text-end">
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger text-decoration-none"
                    onClick={() => setDeleteTarget(key)}
                    disabled={isSaving}
                  >
                    <i className="bi bi-trash me-1" />
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {instructionsPageOn ? (
        <p className="text-muted small">
          Need help connecting? Open the{" "}
          <a href={instructionsHref} target="_blank" rel="noreferrer">
            SFTP setup instructions
          </a>
          . They include your username and host, and can be saved as a PDF to forward to IT or your Slate administrator.
        </p>
      ) : (
        <p className="text-muted small">
          Need help connecting? Download our{" "}
          <Button
            variant="link"
            className="p-0 align-baseline small"
            onClick={downloadInstructions}
          >
            SFTP setup instructions
          </Button>
          .
        </p>
      )}

      <ModalDelete
        title="Key"
        objectLabel={deleteTarget?.label ?? ""}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setDeleteTarget(null);
        }}
        isLoading={isSaving}
      />
    </div>
  );
};

export default OrganizationSftpTab;
